import React, { useCallback, useEffect, useState } from 'react';
import {
  getDivergenciasDoQualificado,
  DivergenciaDoQualificado,
} from '../../../api/comissionamentosFotoApi';
// Faixas compartilhadas do sistema: atenção e erro. Nenhum estilo próprio aqui — a primeira
// versão desta entrega recriou a faixa âmbar do zero, contra a diretriz de reaproveitamento.
import '../../../shared/feedback/aviso-de-tela.css';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * Aviso de que a fotografia de uma competência e o estado de hoje não batem
 * (comissionamentos/at-04).
 *
 * É o pedido do Victor em 18/09/2026: *"podemos pagar um colaborador um valor, e mês que vem
 * perceber que alguns boletos não fazem mais parte daquele recorte, portanto pagamos a mais"*.
 * A fotografia guarda o que foi gerado quando a regra nasceu; este aviso é o que faz alguém
 * olhar para ela antes de o erro virar dinheiro.
 *
 * 🔴 Quatro estados, e nenhum pode se parecer com o outro (regra da at-60, aqui em dobro — a
 * tela decide conferência de pagamento):
 *
 *   | Estado              | O que aparece                                                 |
 *   |---------------------|---------------------------------------------------------------|
 *   | Nenhuma divergência | **Nada.** Silêncio é o estado normal e não merece ruído        |
 *   | Há divergência      | A faixa de atenção, com competência, boletos e a diferença     |
 *   | Falha ao consultar  | Faixa de ERRO dizendo que NÃO foi possível verificar           |
 *   | Não instalado       | **Nada** — o `.sql` do módulo não foi aplicado neste ambiente  |
 *
 * ⚠️ O terceiro jamais pode virar o primeiro: sumir em silêncio depois de um erro diria
 * "está tudo certo" sobre uma conferência que não aconteceu. E o quarto é silêncio de
 * propósito: feature que não existe não é alarme.
 *
 * ⚠️ E os dois números do aviso têm frescores diferentes, por isso são ditos de formas
 * diferentes: os boletos que deixaram de valer são medidos AGORA; o valor atual é o do
 * último processamento, e a data dele vai junto — sem ela, um número de três semanas atrás
 * passaria por atual (foi o que aconteceu, `BUG-006`).
 */

interface Props {
  qualificadoTipo: string;
  qualificadoUuid?: string;
  /** Abre o detalhe de uma competência (o modal da aba Regras). Sem ele, o aviso só informa. */
  onAbrirCompetencia?: (periodo: string) => void;
}

const brl = (v: string | number) =>
  Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataCurta = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
};

const AvisoDeDivergencia: React.FC<Props> = ({
  qualificadoTipo,
  qualificadoUuid,
  onAbrirCompetencia,
}) => {
  const [itens, setItens] = useState<DivergenciaDoQualificado[]>([]);
  const [erro, setErro] = useState(false);
  const [indisponivel, setIndisponivel] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    if (!qualificadoUuid) return;
    setCarregando(true);
    try {
      const r = await getDivergenciasDoQualificado(qualificadoTipo, qualificadoUuid);
      // A fotografia ainda não foi instalada neste ambiente: a feature não existe, e o certo
      // é o silêncio — não um aviso de erro sobre algo que ninguém ligou ainda.
      setIndisponivel(r.disponivel === false);
      setItens(r.data ?? []);
      setErro(false);
    } catch {
      // Estado seguro é `erro`, nunca "lista vazia": vazio significa "conferi e está tudo
      // certo", que é exatamente a afirmação que a falha não autoriza.
      setItens([]);
      setErro(true);
    } finally {
      setCarregando(false);
    }
  }, [qualificadoTipo, qualificadoUuid]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (carregando || !qualificadoUuid || indisponivel) return null;

  if (erro) {
    return (
      <div className="erro-de-tela" role="status">
        Não foi possível verificar se alguma competência mudou desde o cálculo. Recarregue a
        página; se persistir, acione a TI.
      </div>
    );
  }

  if (itens.length === 0) return null;

  return (
    <div className="aviso-de-tela" role="status">
      <strong className="aviso-de-tela-titulo">
        Vale conferir {itens.length === 1 ? 'esta competência' : 'estas competências'}
      </strong>
      <ul>
        {itens.map((d) => {
          const calculadoEm = dataCurta(d.valorAtualCalculadoEm);
          const delta = Number(d.delta);
          return (
            <li key={d.periodo}>
              <strong>{d.periodo}</strong>{' '}
              {d.boletosQueNaoValemMais > 0 ? (
                <>
                  {d.boletosQueNaoValemMais} de {d.boletosNaFoto}{' '}
                  {d.boletosQueNaoValemMais === 1 ? 'boleto' : 'boletos'} usados no cálculo{' '}
                  {d.boletosQueNaoValemMais === 1 ? 'deixou' : 'deixaram'} de valer.{' '}
                </>
              ) : null}
              {delta !== 0 ? (
                <>
                  O cálculo era {brl(d.valorNaFoto)} e hoje é {brl(d.valorAtual)}
                  {calculadoEm ? ` (apurado em ${calculadoEm})` : ' (ainda não reprocessado)'}.{' '}
                </>
              ) : null}
              {onAbrirCompetencia ? (
                <button
                  type="button"
                  className="aviso-de-tela-link"
                  onClick={() => onAbrirCompetencia(d.periodo)}
                >
                  Ver o que mudou
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AvisoDeDivergencia;
